import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customerService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
	ArrowLeftIcon,
	MagnifyingGlassIcon,
	ArrowPathIcon,
	EyeIcon,
	PencilIcon,
	TrashIcon,
	ShoppingBagIcon,
	CalendarIcon,
	CurrencyDollarIcon,
	UserIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const CustomerOrders = () => {
	const { customerId } = useParams();
	const { hasRole } = useAuth();
	const [customer, setCustomer] = useState(null);
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [statusFilter, setStatusFilter] = useState('');
	const [expandedRows, setExpandedRows] = useState([]);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	useEffect(() => {
		const loadCustomerAndOrders = async () => {
			try {
				setLoading(true);
				const [customerData, ordersData] = await Promise.all([
					                                                     customerService.getCustomer(customerId),
					                                                     customerService.getCustomerOrders(customerId, {
						                                                     page: currentPage,
						                                                     status: statusFilter,
						                                                     per_page: 15
					                                                     })
				                                                     ]);

				setCustomer(customerData.data);
				setOrders(ordersData.data?.data || []);
				setTotalPages(ordersData.data?.last_page || 1);
				setCurrentPage(ordersData.data?.current_page || 1);
				setError(null);
			} catch (err) {
				console.error('Error loading customer orders:', err);
				setError('Failed to load customer orders');
				toast.error('Failed to load customer orders');
			} finally {
				setLoading(false);
			}
		};

		if (customerId) {
			loadCustomerAndOrders();
		}
	}, [customerId, currentPage, statusFilter]);

	// Handle page change
	const handlePageChange = (pageNumber) => {
		if (pageNumber >= 1 && pageNumber <= totalPages) {
			setCurrentPage(pageNumber);
		}
	};

	// Toggle row expansion
	const toggleRowExpansion = (orderId) => {
		setExpandedRows(prevExpandedRows =>
			                prevExpandedRows.includes(orderId)
			                ? prevExpandedRows.filter(id => id !== orderId)
			                : [...prevExpandedRows, orderId]
		);
	};

	// Get status badge color
	const getStatusBadgeColor = (status) => {
		switch (status) {
			case 'pending':
				return 'bg-yellow-100 text-yellow-800';
			case 'processing':
				return 'bg-blue-100 text-blue-800';
			case 'fulfilled':
				return 'bg-green-100 text-green-800';
			case 'cancelled':
				return 'bg-red-100 text-red-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	// Format currency
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(amount || 0);
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return format(new Date(dateString), 'MMM d, yyyy');
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	if (!customer) {
		return (
			<div className="text-center py-12">
				<UserIcon className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">Customer not found</h3>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-4">
					<Link
						to="/customers"
						className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
					>
						<ArrowLeftIcon className="h-4 w-4 mr-1" />
						Back to Customers
					</Link>
				</div>
			</div>

			{/* Customer Header */}
			<div className="bg-white shadow rounded-lg p-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
							<UserIcon className="h-8 w-8 text-gray-600" />
						</div>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">
								{customer.customer_info?.customer_name || 'Unknown Customer'}
							</h1>
							<p className="text-gray-600">{customer.customer_info?.customer_email}</p>
							{customer.customer_info?.customer_username && (
								<p className="text-sm text-gray-500">@{customer.customer_info.customer_username}</p>
							)}
						</div>
					</div>
					<div className="flex space-x-3">
						<Link
							to={`/customers/${customerId}/analytics`}
							className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
						>
							View Analytics
						</Link>
						{hasRole('admin') && (
							<Link
								to={`/customers/${customerId}/edit`}
								className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
							>
								Edit Customer
							</Link>
						)}
					</div>
				</div>

				{/* Customer Stats */}
				<div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
					<div className="bg-gray-50 p-4 rounded-lg">
						<div className="flex items-center">
							<ShoppingBagIcon className="h-5 w-5 text-gray-400 mr-2" />
							<div>
								<p className="text-sm text-gray-600">Total Orders</p>
								<p className="text-lg font-semibold">{customer.statistics?.total_orders || 0}</p>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 p-4 rounded-lg">
						<div className="flex items-center">
							<CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
							<div>
								<p className="text-sm text-gray-600">Total Spent</p>
								<p className="text-lg font-semibold">{formatCurrency(customer.statistics?.total_spent)}</p>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 p-4 rounded-lg">
						<div className="flex items-center">
							<CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
							<div>
								<p className="text-sm text-gray-600">Average Order</p>
								<p className="text-lg font-semibold">{formatCurrency(customer.statistics?.average_order_value)}</p>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 p-4 rounded-lg">
						<div className="flex items-center">
							<CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
							<div>
								<p className="text-sm text-gray-600">Last Order</p>
								<p className="text-lg font-semibold">{formatDate(customer.statistics?.last_order_date)}</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="bg-white p-4 rounded-lg shadow">
				<div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
					<div className="w-full md:w-1/4">
						<label htmlFor="statusFilter" className="sr-only">
							Status
						</label>
						<select
							id="statusFilter"
							name="statusFilter"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
						>
							<option value="">All Statuses</option>
							<option value="pending">Pending</option>
							<option value="processing">Processing</option>
							<option value="fulfilled">Fulfilled</option>
							<option value="cancelled">Cancelled</option>
						</select>
					</div>

					<div className="w-full md:w-auto">
						<button
							onClick={() => {
								setStatusFilter('');
								setCurrentPage(1);
							}}
							className="w-full md:w-auto inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
						>
							<ArrowPathIcon className="h-5 w-5 mr-2" /> Reset Filters
						</button>
					</div>
				</div>
			</div>

			{/* Orders Table - Desktop */}
			<div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-md">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
					<tr>
						<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Order
						</th>
						<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Date
						</th>
						<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Status
						</th>
						<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Total
						</th>
						<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Items
						</th>
						<th scope="col" className="relative px-6 py-3">
							<span className="sr-only">Actions</span>
						</th>
					</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
					{orders.map((order) => (
						<React.Fragment key={order.id}>
							<tr className="hover:bg-gray-50">
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm font-medium text-gray-900">
										{order.order_number}
									</div>
									<div className="text-sm text-gray-500">
										ID: {order.id}
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
									{formatDate(order.order_date)}
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                                            {order.status}
                                        </span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
									{formatCurrency(order.order_total)}
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
									{order.items?.length || 0} items
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
									<div className="flex items-center space-x-2 justify-end">
										<button
											onClick={() => toggleRowExpansion(order.id)}
											className="text-primary-600 hover:text-primary-900"
											title="View Details"
										>
											<EyeIcon className="h-4 w-4" />
										</button>
										<Link
											to={`/orders/${order.id}`}
											className="text-blue-600 hover:text-blue-900"
											title="View Order"
										>
											<ShoppingBagIcon className="h-4 w-4" />
										</Link>
										{hasRole('admin') && (
											<Link
												to={`/orders/${order.id}/edit`}
												className="text-indigo-600 hover:text-indigo-900"
												title="Edit Order"
											>
												<PencilIcon className="h-4 w-4" />
											</Link>
										)}
									</div>
								</td>
							</tr>
							{expandedRows.includes(order.id) && order.items && (
								<tr>
									<td colSpan="6" className="px-6 py-4 bg-gray-50">
										<div>
											<h4 className="text-sm font-medium text-gray-900 mb-2">Order Items</h4>
											<div className="space-y-2">
												{order.items.map((item, index) => (
													<div key={index} className="flex justify-between items-center text-sm">
                                                            <span className="text-gray-600">
                                                                {item.product?.name || item.name} ({item.variant})
                                                            </span>
														<span className="text-gray-900">
                                                                Qty: {item.quantity}
                                                            </span>
													</div>
												))}
											</div>
										</div>
									</td>
								</tr>
							)}
						</React.Fragment>
					))}
					</tbody>
				</table>
			</div>

			{/* Orders List - Mobile */}
			<div className="md:hidden space-y-4">
				{orders.map((order) => (
					<div key={order.id} className="bg-white shadow rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<div>
								<p className="text-sm font-medium text-gray-900">{order.order_number}</p>
								<p className="text-xs text-gray-500">ID: {order.id}</p>
							</div>
							<button
								onClick={() => toggleRowExpansion(order.id)}
								className="text-primary-600 hover:text-primary-900"
							>
								<EyeIcon className="h-5 w-5" />
							</button>
						</div>

						<div className="grid grid-cols-2 gap-4 text-sm">
							<div>
								<p className="text-gray-500">Date</p>
								<p className="font-medium">{formatDate(order.order_date)}</p>
							</div>
							<div>
								<p className="text-gray-500">Status</p>
								<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                                    {order.status}
                                </span>
							</div>
							<div>
								<p className="text-gray-500">Total</p>
								<p className="font-medium">{formatCurrency(order.order_total)}</p>
							</div>
							<div>
								<p className="text-gray-500">Items</p>
								<p className="font-medium">{order.items?.length || 0} items</p>
							</div>
						</div>

						{expandedRows.includes(order.id) && (
							<div className="mt-4 pt-4 border-t border-gray-200">
								<div className="flex flex-wrap gap-2 mb-3">
									<Link
										to={`/orders/${order.id}`}
										className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
									>
										View Order
									</Link>
									{hasRole('admin') && (
										<Link
											to={`/orders/${order.id}/edit`}
											className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
										>
											Edit
										</Link>
									)}
								</div>
								{order.items && order.items.length > 0 && (
									<div>
										<h5 className="text-sm font-medium text-gray-900 mb-2">Items</h5>
										<div className="space-y-1">
											{order.items.map((item, index) => (
												<div key={index} className="text-xs text-gray-600">
													{item.product?.name || item.name} ({item.variant}) - Qty: {item.quantity}
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				))}
			</div>

			{/* Pagination */}
			{orders.length > 0 && (
				<div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
					<div className="flex-1 flex justify-between sm:hidden">
						<button
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage <= 1}
							className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Previous
						</button>
						<button
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage >= totalPages}
							className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Next
						</button>
					</div>
					<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
						<div>
							<p className="text-sm text-gray-700">
								Page <span className="font-medium">{currentPage}</span> of{' '}
								<span className="font-medium">{totalPages}</span>
							</p>
						</div>
						<div>
							<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
								<button
									onClick={() => handlePageChange(currentPage - 1)}
									disabled={currentPage <= 1}
									className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Previous
								</button>
								{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
									const pageNum = currentPage - 2 + i;
									if (pageNum < 1 || pageNum > totalPages) return null;
									return (
										<button
											key={pageNum}
											onClick={() => handlePageChange(pageNum)}
											className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
												pageNum === currentPage
												? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
												: 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
											}`}
										>
											{pageNum}
										</button>
									);
								})}
								<button
									onClick={() => handlePageChange(currentPage + 1)}
									disabled={currentPage >= totalPages}
									className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Next
								</button>
							</nav>
						</div>
					</div>
				</div>
			)}

			{orders.length === 0 && !loading && (
				<div className="text-center py-12">
					<ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
					<h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
					<p className="mt-1 text-sm text-gray-500">
						{statusFilter ? 'Try adjusting your filter criteria.' : 'This customer has no orders yet.'}
					</p>
				</div>
			)}
		</div>
	);
};

export default CustomerOrders;
